import { Link } from "react-router-dom";
import {
  ArrowDownToLine,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Plus,
  TrendingUp,
  Wallet as WalletIcon,
} from "lucide-react";
import { useSession } from "../lib/session";
import { useDailySales, useOrders, useSummary, useWallet } from "../lib/queries";
import { greeting, longDate, tzs } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";

export function Dashboard() {
  const { user, agent } = useSession();
  const wallet = useWallet();
  const summary = useSummary("daily");
  const daily = useDailySales(7);
  const recent = useOrders({ limit: 6 });

  const name = user?.first_name || agent?.business_name || "Agent";
  const balances = wallet.data;
  const sales = summary.data?.sales;
  const successRate = sales?.count ? Math.round((sales.success / sales.count) * 100) : 0;
  const bars = daily.data?.days ?? [];
  const maxGross = Math.max(1, ...bars.map((b) => Number(b.gross_minor)));

  return (
    <div className="stack-6">
      <div className="page-head">
        <div>
          <h2 className="ev-h2">{greeting()}, {name}</h2>
          <p className="ev-muted">{longDate()} · {agent?.business_name || "Your business"}</p>
        </div>
        <div className="page-head-actions">
          <Link to="/wallet" className="ev-btn ev-btn-sm ev-btn-secondary">
            <ArrowDownToLine size={15} /> Top up
          </Link>
          <Link to="/sell" className="ev-btn ev-btn-sm ev-btn-primary">
            <Plus size={15} /> New sale
          </Link>
        </div>
      </div>

      <div className="wallet-layout">
        <div className="stack-5">
          <section className="wallet-hero">
            <div className="wallet-hero-top">
              <span className="wallet-hero-label"><WalletIcon size={16} /> Available balance</span>
              <span className="wallet-hero-badge"><span className="ev-dot" /> {wallet.isError ? "Offline" : "Live"}</span>
            </div>
            <div className="wallet-hero-amount">
              {wallet.isLoading ? "—" : tzs(balances?.available_minor ?? 0)}
            </div>
            <p className="wallet-hero-sub">Agent wallet · TZS · {agent?.code ?? "—"}</p>
            <div className="wallet-hero-grid">
              <div><span>Reserved</span><strong>{tzs(balances?.reserved_minor ?? 0)}</strong></div>
              <div><span>Commission bucket</span><strong>{tzs(balances?.commission_minor ?? 0)}</strong></div>
              <div><span>Daily limit</span><strong>{tzs(agent?.limit_daily_minor ?? 0)}</strong></div>
            </div>
            <div className="wallet-hero-foot">
              <CheckCircle2 size={15} /> Reserve-first selling — funds move before the network call
            </div>
          </section>

          <div className="kpi-grid">
            <section className="ev-card kpi-card">
              <span className="ev-eyebrow">Today's sales</span>
              <strong className="kpi-value">{tzs(sales?.gross_minor ?? 0)}</strong>
              <span className="kpi-foot">
                <span className="kpi-delta"><TrendingUp size={13} /> {sales?.count ?? 0} orders</span>
              </span>
            </section>
            <section className="ev-card kpi-card">
              <span className="ev-eyebrow">Commission</span>
              <strong className="kpi-value ev-pos">{tzs(summary.data?.commission_minor ?? 0)}</strong>
              <span className="kpi-foot"><span className="ev-caption">earned on successful sales</span></span>
            </section>
            <section className="ev-card kpi-card">
              <span className="ev-eyebrow">Success rate</span>
              <strong className="kpi-value">{successRate}%</strong>
              <span className="kpi-foot">
                <span className="ev-caption">{sales?.success ?? 0} sent · {sales?.failed ?? 0} failed</span>
              </span>
            </section>
            <section className="ev-card kpi-card">
              <span className="ev-eyebrow">Average sale</span>
              <strong className="kpi-value">{tzs(summary.data?.average_sale_minor ?? 0)}</strong>
              <span className="kpi-foot"><span className="ev-caption">per successful transaction</span></span>
            </section>
          </div>

          <section className="ev-card ev-card-pad">
            <div className="ev-row-between">
              <div>
                <span className="ev-eyebrow">Sales trend</span>
                <h3 className="ev-h3" style={{ marginTop: 4 }}>Gross value · last 7 days</h3>
              </div>
              <Link to="/reports" className="ev-body-link">Full report <ArrowRight size={14} /></Link>
            </div>
            <div className="bar-chart bar-chart-tall" role="img" aria-label="Gross sales by day">
              {bars.map((bar) => (
                <div className="bar-col" key={bar.date}>
                  <span className="bar-value">{(Number(bar.gross_minor) / 100_000).toFixed(1)}</span>
                  <div className="bar-track">
                    <span className="bar" style={{ height: `${(Number(bar.gross_minor) / maxGross) * 100}%` }} />
                  </div>
                  <span className="bar-day">
                    {new Date(bar.date).toLocaleDateString("en-GB", { weekday: "short" })}
                  </span>
                </div>
              ))}
              {bars.length === 0 && <p className="ev-muted">No sales in the last seven days yet.</p>}
            </div>
            <div className="chart-foot">
              <span><strong>{tzs(daily.data?.gross_minor ?? 0)}</strong> this week</span>
              <span className="ev-badge ev-badge-info">{daily.data?.orders ?? 0} orders</span>
            </div>
          </section>

          <section className="ev-card">
            <div className="ev-card-head">
              <div>
                <h3 className="ev-card-title">Recent transactions</h3>
                <p className="ev-caption">Every sale, newest first.</p>
              </div>
              <Link to="/transactions" className="ev-body-link">View all <ArrowUpRight size={14} /></Link>
            </div>
            <div className="ev-table-wrap">
              <table className="ev-table recent-table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Customer</th>
                    <th>Network</th>
                    <th className="ev-num">Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(recent.data?.data ?? []).map((order) => (
                    <tr key={order.ref}>
                      <td><span className="ev-mono tx-ref">{order.ref}</span></td>
                      <td className="ev-muted">{order.msisdn_masked}</td>
                      <td>{order.mno.name}</td>
                      <td className="ev-num ev-money">{tzs(order.amount_minor)}</td>
                      <td><StatusBadge status={order.status} /></td>
                    </tr>
                  ))}
                  {!recent.isLoading && (recent.data?.data ?? []).length === 0 && (
                    <tr>
                      <td colSpan={5}>
                        <div className="ev-empty">
                          <Clock />
                          <strong>No sales yet</strong>
                          <p className="ev-caption">Your first sale will appear here.</p>
                          <Link to="/sell" className="ev-btn ev-btn-sm ev-btn-primary">Sell airtime</Link>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <aside className="stack-5">
          <section className="ev-card ev-card-pad">
            <span className="ev-eyebrow">Account</span>
            <div className="bank-row">
              <span className="bank-mark"><CheckCircle2 size={18} /></span>
              <div>
                <strong>{agent?.business_name || "Agent"}</strong>
                <span>{agent?.region || "—"} · {agent?.tier ?? "standard"} tier</span>
              </div>
              <span className={`ev-badge ${agent?.kyc_status === "approved" ? "ev-badge-success" : "ev-badge-pending"}`}>
                {agent?.kyc_status === "approved" ? "KYC approved" : "KYC pending"}
              </span>
            </div>
            <Link to="/profile" className="ev-btn ev-btn-sm ev-btn-secondary ev-btn-block" style={{ marginTop: 14 }}>
              Manage profile
            </Link>
          </section>

          <section className="ev-card ev-card-pad">
            <span className="ev-eyebrow">Limits</span>
            <h3 className="ev-h3" style={{ marginTop: 4 }}>Daily allowance</h3>
            <div className="wallet-limit-grid" style={{ marginTop: 12 }}>
              <div>
                <div className="ev-row-between">
                  <span className="ev-caption">Sold today</span>
                  <strong className="ev-caption ev-ink">{tzs(sales?.gross_minor ?? 0)} / {tzs(agent?.limit_daily_minor ?? 0)}</strong>
                </div>
                <div className="limit-track">
                  <span style={{ width: `${Math.min(100, ((Number(sales?.gross_minor) || 0) / Math.max(1, Number(agent?.limit_daily_minor) || 1)) * 100)}%` }} />
                </div>
              </div>
              <div>
                <div className="ev-row-between">
                  <span className="ev-caption">Single sale cap</span>
                  <strong className="ev-caption ev-ink">{tzs(agent?.limit_txn_minor ?? 0)}</strong>
                </div>
                <div className="limit-track"><span style={{ width: "40%" }} /></div>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
